import {
  Background,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  getNodesBounds,
  getViewportForBounds,
  useNodesInitialized,
  useReactFlow,
  useStore,
  useViewport,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeMouseHandler,
  type NodeTypes,
} from '@xyflow/react';
import dagre from 'dagre';
import { observer } from 'mobx-react-lite';
import {
  FC,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';

import {
  Button,
  Checkbox,
  Icon,
  TextBody,
  sendToast,
} from '@pypestream/design-system';
import { tableStore } from '../../stores/table-store';
import { resolveBobcatAccess } from '../../utils/bobcat-flags';
import { NodePropertyPanel } from '../../components/table/node-property-panel/node-property-panel';
import { ResourceLibPanel } from '../../components/table/resource-lib/resource-lib-panel';
import { ResourceLibraryFab } from '../../components/table/resource-lib/resource-library-fab';
import {
  scriptNamesFor,
  uploadResourceLibScripts,
} from '../../components/table/resource-lib/upload-resource-lib-scripts';
import { useUploadPythonFiles } from '../../hooks';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import {
  AgentNodeCard,
  cardHeight,
  cardRowCenterY,
  cardWidth,
  type AgentNodeData,
} from './agent-node-card';
import {
  AgentSidebar,
  matchesFilter,
  SUGGEST_AGENTS_FROM,
} from './agent-sidebar';
import {
  routeWires,
  type RoutableCard,
  type RoutableEdge,
} from './canvas-edge-routing';
import {
  buildCanvasEdgeHops,
  buildCanvasEdges,
  litAgentIds,
  type CanvasEdge,
  type CanvasEdgeKind,
} from './canvas-edges-model';
import {
  buildCanvasAgentNodes,
  buildUngroupedSummary,
  buildUngroupedNodes,
  type CanvasAgentNode,
} from './canvas-view-model';
import { RoutedEdge, type RoutedEdgeData, type WireDim } from './routed-edge';

import '@xyflow/react/dist/style.css';
import './canvas-view.scss';

const NODE_TYPES: NodeTypes = {
  agent: AgentNodeCard,
};

const EDGE_TYPES: EdgeTypes = {
  routed: RoutedEdge,
};

// Past a few dozen the labels stop being readable and start being texture, so
// beyond that the canvas drops them and you select an agent to read its edges
// — the prototype's own rule (`drawCanvas`).
const MAX_LABELLED_WIRES = 40;

// Never zoom a small solution up past 1:1 — two cards fitted to the viewport
// render at poster size, which reads as a broken page, not a diagram.
const FIT_VIEW_OPTIONS = { padding: 0.15, maxZoom: 1 };
const MIN_ZOOM = 0.2;

// The gutters dagre leaves: `nodesep` between cards in a column (the gutter an
// edge label sits in) and `ranksep` between columns (room for a corridor).
const NODE_SEP = 26;
// Wide enough for the Supervisor's fan of lanes (`FAN_START` + a `FAN_GAP`
// per intent, canvas-edge-routing.ts) and then a label hung on each line's
// last leg beside the card it reaches: `.c-canvas-label`'s max-width
// (canvas-view.scss) fits in what is left — change them together.
const RANK_SEP = 240;

interface LayoutOptions {
  agents: CanvasAgentNode[];
  /** Real edges between visible cards — dagre ranks cards along them. */
  layoutEdges: CanvasEdge[];
  selectedAgentId: string | null;
  /** Phase 3 build A: render each card's member rows and wire their click. */
  showMembers: boolean;
  onMemberClick?: (rowId: string) => void;
  /** Phase 3 build B: the sidebar's per-agent Collapse/Expand. */
  collapsedAgentIds?: Set<string>;
  /** Phase 3 build B: color chips by node type and draw the type strip. */
  showNodeTypeDots?: boolean;
  /** Phase 3 build B: the sidebar filter's de-emphasis, per agent. */
  dimmedAgentIds?: Set<string>;
  /** Phase 3 build C: not connected to the selected agent. */
  routingDimmedAgentIds?: Set<string>;
  /** The two ends of the edge whose label is hovered, while tracing. */
  traceEnds?: Set<string>;
  /** Use-Case Agents no visible edge reaches. */
  unroutedAgentIds?: Set<string>;
  /** A Resource Library item is being dragged over the canvas. */
  isDraggingResource?: boolean;
  /** The card the dragged item is over. */
  dropOverAgentId?: string | null;
  /** Rows just placed from the Resource Library. */
  freshRowIds?: Set<string>;
  /** Canvas editing, for someone who can edit with the flag on. */
  editing?: CanvasEditing;
  /** Open or close one card (its header caret). */
  onToggleCollapse?: (agentId: string) => void;
  /** "Explain this agent" (`explain-agent-panel.tsx`). */
  onExplain?: (agentId: string) => void;
}

interface CanvasEditing {
  onAddNode: (agentId: string) => void;
  onDeleteAgent: (agentId: string) => void;
  onDeleteNode: (rowId: string) => void;
}

/**
 * The Supervisor's card. Core cards are exactly the two auto-group names
 * (`agentCategoryFor`), so the title tells the two apart.
 */
const isSupervisorCard = (agent: CanvasAgentNode): boolean =>
  agent.category === 'core' && /supervisor/i.test(agent.title);

/**
 * Turn the flat agent list into positioned React Flow nodes, plus the same
 * geometry in the shape the edge router needs.
 *
 * No persisted layout exists anywhere in a solution's data, so dagre places
 * the cards, left to right, ranked along the solution's real edges (every
 * visible edge, not just the ones currently drawn — so selecting an agent or
 * flipping error routing never moves a card). Each card's size is exact (see
 * `agent-node-card.tsx`'s `CARD`) and set on the node itself, so what dagre
 * placed, what renders and where the edges attach all agree.
 */
const layoutAgentNodes = ({
  agents,
  layoutEdges,
  selectedAgentId,
  showMembers,
  onMemberClick,
  collapsedAgentIds,
  onToggleCollapse,
  showNodeTypeDots,
  dimmedAgentIds,
  routingDimmedAgentIds,
  traceEnds,
  unroutedAgentIds,
  isDraggingResource,
  dropOverAgentId,
  freshRowIds,
  editing,
  onExplain,
}: LayoutOptions): { nodes: Node[]; cards: RoutableCard[] } => {
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({ rankdir: 'LR', nodesep: NODE_SEP, ranksep: RANK_SEP });
  graph.setDefaultEdgeLabel(() => ({}));

  const sized = agents.map((agent) => {
    const showsMembers = showMembers && !collapsedAgentIds?.has(agent.id);
    const hasStrip = !!showNodeTypeDots && agent.members.length > 0;
    const openRows = showsMembers ? agent.members.length : 0;
    return {
      agent,
      showsMembers,
      hasStrip,
      w: cardWidth(agent.category),
      h: cardHeight(agent.category, openRows, hasStrip),
    };
  });

  sized.forEach(({ agent, w, h }) =>
    graph.setNode(agent.id, { width: w, height: h })
  );
  layoutEdges.forEach((edge) => graph.setEdge(edge.source, edge.target));

  dagre.layout(graph);

  const cards: RoutableCard[] = [];
  const nodes = sized.map(({ agent, showsMembers, hasStrip, w, h }) => {
    const { x: cx, y: cy } = graph.node(agent.id);
    // dagre centres a node on (x, y); React Flow positions by top-left.
    const x = cx - w / 2;
    const y = cy - h / 2;

    const rowY = new Map<string, number>();
    if (showsMembers) {
      agent.members.forEach((member, i) =>
        rowY.set(member.id, y + cardRowCenterY(agent.category, i, hasStrip))
      );
    }
    cards.push({ id: agent.id, x, y, w, h, rowY });

    const tracing = !!traceEnds;
    const data: AgentNodeData = {
      title: agent.title,
      color: agent.color,
      nodeCount: agent.nodeCount,
      category: agent.category,
      isSelected: agent.id === selectedAgentId,
      members: showsMembers ? agent.members : undefined,
      memberTypes: showNodeTypeDots
        ? agent.members.map((m) => m.nodeType)
        : undefined,
      onMemberClick,
      showNodeTypeDots,
      isDimmed: dimmedAgentIds?.has(agent.id),
      isRoutingDimmed: routingDimmedAgentIds?.has(agent.id),
      isUnrouted: unroutedAgentIds?.has(agent.id),
      source: agent.source,
      isTraceDimmed: tracing && !traceEnds.has(agent.id),
      isTraceHot: tracing && traceEnds.has(agent.id),
      isDropTarget: isDraggingResource,
      isDropOver: isDraggingResource && agent.id === dropOverAgentId,
      freshRowIds,
      ...(onToggleCollapse && showMembers && agent.members.length > 0
        ? {
            collapsed: !showsMembers,
            onToggleCollapse: () => onToggleCollapse(agent.id),
          }
        : {}),
      ...(onExplain ? { onExplain: () => onExplain(agent.id) } : {}),
      ...(editing
        ? {
            onAddNode: () => editing.onAddNode(agent.id),
            // The solution needs its Supervisor and Knowledge Agent.
            onDeleteAgent:
              agent.category === 'core'
                ? undefined
                : () => editing.onDeleteAgent(agent.id),
            onDeleteNode: editing.onDeleteNode,
            connectable: isSupervisorCard(agent)
              ? ('source' as const)
              : agent.category === 'core'
                ? undefined
                : ('target' as const),
          }
        : {}),
    };

    return {
      id: agent.id,
      type: 'agent',
      position: { x, y },
      width: w,
      height: h,
      style: { width: w, height: h },
      data,
      draggable: false,
      // Stays false (Phase 1's decision) — a single click-to-toggle read by
      // `onNodeClick` below, not React Flow's own selection model.
      selectable: false,
    } satisfies Node;
  });

  return { nodes, cards };
};

/** One drawn line: every visible edge between the same two agents, of the
 *  same kind, drawn once. Its label is the first edge's, with a count of the
 *  rest ("chit_chat +2"): all of them joined ran past the label's width and
 *  read as one long intent. The full list is in `labels` and the tooltip. */
interface DrawnWire extends RoutableEdge {
  kind: CanvasEdgeKind;
  label?: string;
  labels: string[];
  /** Every edge merged into this wire is phrase-matched, none stored. */
  inferred?: boolean;
}

const mergeWires = (
  edges: CanvasEdge[],
  hops: ReturnType<typeof buildCanvasEdgeHops>
): DrawnWire[] => {
  const byKey = new Map<string, { wire: DrawnWire; labels: string[] }>();
  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}:${edge.kind}`;
    const hop = hops.get(edge.id);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        wire: {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          kind: edge.kind,
          sourceRowId: hop?.sourceRowId,
          targetRowId: hop?.targetRowId,
          inferred: !!edge.inferred,
          labels: [],
        },
        labels: edge.label ? [edge.label] : [],
      });
      return;
    }
    existing.wire.inferred = existing.wire.inferred && !!edge.inferred;
    // More than one edge on this pair: keep a row only if every one of them
    // leaves (or lands on) that same row; otherwise fall back to the card.
    if (existing.wire.sourceRowId !== hop?.sourceRowId) {
      existing.wire.sourceRowId = undefined;
    }
    if (existing.wire.targetRowId !== hop?.targetRowId) {
      existing.wire.targetRowId = undefined;
    }
    if (edge.label && !existing.labels.includes(edge.label)) {
      existing.labels.push(edge.label);
    }
  });
  return [...byKey.values()].map(({ wire, labels }) => ({
    ...wire,
    labels,
    label: labels.length
      ? labels[0] + (labels.length > 1 ? ` +${labels.length - 1}` : '')
      : undefined,
  }));
};

/** The canvas-only controls on the right of the Build topbar. */
const CanvasControls: FC<{
  showFind: boolean;
  filterText: string;
  onFilterChange: (value: string) => void;
  showErrorEdges: boolean;
  onToggleErrorEdges: () => void;
  showZoom: boolean;
}> = ({
  showFind,
  filterText,
  onFilterChange,
  showErrorEdges,
  onToggleErrorEdges,
  showZoom,
}) => {
  const { zoomIn, zoomOut, getNodes, setViewport } = useReactFlow();
  const { zoom } = useViewport();
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);

  // Computed here rather than via `fitView()`: with controlled `nodes` and no
  // `onNodesChange`, React Flow 12's queued `fitView()` never resolves (its
  // node queue hands the no-op diff to the missing handler and never refits).
  const fit = () =>
    setViewport(
      getViewportForBounds(
        getNodesBounds(getNodes()),
        width,
        height,
        MIN_ZOOM,
        FIT_VIEW_OPTIONS.maxZoom,
        FIT_VIEW_OPTIONS.padding
      )
    );

  return (
    <>
      {showFind && (
        <label className="c-canvas-find">
          <span className="c-canvas-find__label">Find an agent</span>
          <input
            type="search"
            placeholder="Find an agent…"
            autoComplete="off"
            value={filterText}
            data-testid="bobcat-canvas-find"
            onChange={(e) => onFilterChange(e.target.value)}
          />
        </label>
      )}
      <button
        type="button"
        className={`c-canvas-toggle${showErrorEdges ? ' is-on' : ''}`}
        aria-pressed={showErrorEdges}
        data-testid="bobcat-canvas-error-toggle"
        onClick={onToggleErrorEdges}
      >
        <span className="c-canvas-toggle__dot" />
        Error routing
      </button>
      {showZoom && (
        <span className="c-canvas-zoom">
          <button
            type="button"
            aria-label="Zoom out"
            data-testid="bobcat-canvas-zoom-out"
            onClick={() => zoomOut()}
          >
            &minus;
          </button>
          <span
            className="c-canvas-zoom__pct"
            data-testid="bobcat-canvas-zoom-pct"
          >
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            aria-label="Zoom in"
            data-testid="bobcat-canvas-zoom-in"
            onClick={() => zoomIn()}
          >
            +
          </button>
          <button
            type="button"
            className="c-canvas-zoom__fit"
            data-testid="bobcat-canvas-zoom-fit"
            onClick={fit}
          >
            Fit
          </button>
        </span>
      )}
    </>
  );
};

export interface CanvasViewProps {
  /**
   * With `bobcat-five-stage-nav` on, `builder.tsx` hands in the Build topbar;
   * the canvas renders it at the top of its own layout with its controls
   * (find, error routing, zoom) on the right — they have to live inside the
   * canvas's React Flow context to drive the zoom. Absent: the canvas lays
   * itself out exactly as before, under the floating view-mode toggle.
   */
  renderTopbar?: (canvasControls: ReactNode) => ReactNode;
}

/**
 * Build Stage canvas — Phase 1/2/3 builds A+B+C: a pannable, zoomable
 * surface showing each of the solution's real agent groups as a card, its
 * routing edges, a click-through from a member node to the real node detail
 * panel (build A), an agent sidebar with node-type colors and a legend (build
 * B), and — behind ITS own flag — dimming everything not connected to a
 * selected card (build C). Visually it follows the Protolab prototype's Build
 * canvas (`prostudio-build-flow-layouts.html`): compact colored cards, node
 * chips, orthogonal routed edges with label pills, hover-to-trace.
 *
 * Gated by `bobcat-build-canvas` at the call site (`builder.tsx`); checks
 * `canvasNodeDetailPanel`/`canvasAgentSidebar`/`canvasRoutingHighlight`
 * itself since each sub-feature must be switchable independently (see
 * `bobcat-flags.ts`).
 */
export const CanvasView: FC<CanvasViewProps> = (props) => (
  <ReactFlowProvider>
    <CanvasViewInner {...props} />
  </ReactFlowProvider>
);

const CanvasViewInner: FC<CanvasViewProps> = observer(({ renderTopbar }) => {
  const inStage = !!renderTopbar;

  // Computed DURING render, not in a `useMemo` keyed on `rows` — the store's
  // arrays are mutated IN PLACE, so a dep array built from them would never
  // change. `observer` re-renders on the MobX observables these read.
  const rows = tableStore.getAllSortedRows();
  const agents = buildCanvasAgentNodes(rows);
  const ungroupedSummary = buildUngroupedSummary(rows);
  const canvasEdges = buildCanvasEdges(rows);
  const edgeHops = buildCanvasEdgeHops(rows);
  const showUngrouped = tableStore.showUngroupedNodes;
  const ungroupedNodes = buildUngroupedNodes(rows);
  // Groups for a solution built without them (`suggest-agents-panel.tsx`).
  const suggestAgents = tableStore.isViewer
    ? undefined
    : () => tableStore.setSuggestAgentsPanelOpen(true);

  const nodeDetailPanelEnabled =
    resolveBobcatAccess('canvasNodeDetailPanel', tableStore.featureFlags) ===
    'allowed';
  const sidebarEnabled =
    resolveBobcatAccess('canvasAgentSidebar', tableStore.featureFlags) ===
    'allowed';
  const routingHighlightEnabled =
    resolveBobcatAccess('canvasRoutingHighlight', tableStore.featureFlags) ===
    'allowed';

  // Local React state, not MobX: none of it survives a route change.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [showErrorEdges, setShowErrorEdges] = useState(false);
  const [tracingWireId, setTracingWireId] = useState<string | null>(null);

  // Phase 3 build B. `hiddenAgentIds` starts empty (everything visible).
  const [filterText, setFilterText] = useState('');
  const [hiddenAgentIds, setHiddenAgentIds] = useState<Set<string>>(new Set());
  // Which cards show their nodes, as the prototype's `cvOpen`: the Supervisor
  // and Knowledge Agent open, Use-Case Agents closed, until someone opens or
  // closes one (its caret, its sidebar row, selecting it, Expand/Collapse
  // all) — those choices are kept here, per agent.
  const [openOverrides, setOpenOverrides] = useState<Map<string, boolean>>(
    () => new Map()
  );
  const isAgentOpen = (agent: { id: string; category: string }) =>
    openOverrides.get(agent.id) ?? agent.category === 'core';
  const setAgentsOpen = (ids: string[], open: boolean) =>
    setOpenOverrides((current) => {
      const next = new Map(current);
      ids.forEach((id) => next.set(id, open));
      return next;
    });
  const collapsedAgentIds = new Set(
    agents.filter((agent) => !isAgentOpen(agent)).map((agent) => agent.id)
  );
  const toggleAgentOpen = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    if (agent) setAgentsOpen([agentId], !isAgentOpen(agent));
  };

  const setAgentsVisible = (ids: string[], visible: boolean) => {
    setHiddenAgentIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => (visible ? next.delete(id) : next.add(id)));
      return next;
    });
  };
  const toggleAgentVisible = (id: string) =>
    setAgentsVisible([id], hiddenAgentIds.has(id));

  const routedAgentIds = new Set(
    canvasEdges.flatMap((edge) => [edge.source, edge.target])
  );

  const visibleAgents = sidebarEnabled
    ? agents.filter((agent) => !hiddenAgentIds.has(agent.id))
    : agents;
  const visibleAgentIds = new Set(visibleAgents.map((agent) => agent.id));
  // A hidden card's edges would otherwise dangle.
  const visibleEdges = canvasEdges.filter(
    (edge) =>
      visibleAgentIds.has(edge.source) && visibleAgentIds.has(edge.target)
  );
  const dimmedAgentIds = sidebarEnabled
    ? new Set(
        visibleAgents
          .filter((agent) => !matchesFilter(agent, filterText))
          .map((agent) => agent.id)
      )
    : undefined;

  // Phase 3 build C — `lit` reads the FULL edge list, matching the
  // prototype's `cvLit`, so an agent joined to the selection only by a
  // (currently hidden) error edge still lights up.
  const lit = routingHighlightEnabled
    ? litAgentIds(selectedAgentId, canvasEdges)
    : null;
  const routingDimmedAgentIds = lit
    ? new Set(
        visibleAgents.filter((agent) => !lit.has(agent.id)).map((a) => a.id)
      )
    : undefined;

  // Build A: the SAME selection mechanism Table View's row-number click uses.
  const onMemberClick = (rowId: string) => {
    if (!nodeDetailPanelEnabled) return;
    tableStore.deselectAllCells();
    // `selectRow` adds to the selection; two selected rows means no single
    // node for the panel, so it wouldn't open after the first click.
    tableStore.deselectAllRows();
    tableStore.selectRow(rowId);
    tableStore.setExpandedCellViewOpen(true);
  };

  // ---- Resource Library: drop onto an agent card ----
  const { resourceLibraryData } = useProStudioCtxSelector((ctx) => ({
    resourceLibraryData: ctx.resourceLibraryData,
  }));
  const dragDropEnabled =
    resolveBobcatAccess('resourceLibraryDragDrop', tableStore.featureFlags) ===
      'allowed' && !tableStore.isViewer;
  const isDraggingResource =
    dragDropEnabled && tableStore.resourceLibDragData !== null;
  const [dropOverAgentId, setDropOverAgentId] = useState<string | null>(null);
  const [freshRowIds, setFreshRowIds] = useState<Set<string>>(new Set());
  const [pendingFocus, setPendingFocus] = useState<{
    agentId: string;
    rowId: string;
  } | null>(null);
  const { setCenter, getZoom, fitView } = useReactFlow();

  // The `fitView` prop fits once, at React Flow's init — before the cards are
  // measured, and while the stage's panels and the solution's rows are still
  // settling — so the canvas opened parked in a corner at 100%, or shrunk to
  // the 20% floor. Instead, fit whenever the pane's size or the set of cards
  // changes, until the designer pans or zooms; from then on the view is
  // theirs (`onMoveStart` below).
  const nodesInitialized = useNodesInitialized();
  const paneWidth = useStore((st) => st.width);
  const paneHeight = useStore((st) => st.height);
  const userMovedView = useRef(false);
  const fitKey = visibleAgents.map((a) => a.id).join('|');
  useEffect(() => {
    if (userMovedView.current || !nodesInitialized) return undefined;
    if (paneWidth < 50 || paneHeight < 50) return undefined;
    // A timer, not requestAnimationFrame: rAF never fires in a background
    // tab, so a canvas opened there stayed unfitted.
    const timer = window.setTimeout(() => {
      void fitView(FIT_VIEW_OPTIONS);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [nodesInitialized, paneWidth, paneHeight, fitKey, fitView]);
  // A dropped item's scripts upload the same way a clicked one's do.
  const { handleUpload } = useUploadPythonFiles('scriptFile');
  const handleUploadRef = useRef(handleUpload);
  handleUploadRef.current = handleUpload;

  const agentIdFromEvent = (e: DragEvent): string | null =>
    (e.target as HTMLElement | null)
      ?.closest<HTMLElement>('.react-flow__node[data-id]')
      ?.getAttribute('data-id') ?? null;

  const onResourceDragOver = (e: DragEvent) => {
    if (!isDraggingResource) return;
    const agentId = agentIdFromEvent(e);
    if (agentId !== dropOverAgentId) setDropOverAgentId(agentId);
    // Off a card, the drop starts a new agent — only with editing on.
    if (!agentId && !editingEnabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onResourceDragLeave = (e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as HTMLElement | null)) {
      setDropOverAgentId(null);
    }
  };

  // What landed becomes the selection, on the agent that took it, so the drop
  // ends where you can read it (the prototype's `dropOnAgent`).
  const landOn = (agentId: string, newRowIds: string[]) => {
    setAgentsVisible([agentId], true);
    setAgentsOpen([agentId], true);
    setSelectedAgentId(agentId);
    setFreshRowIds(new Set(newRowIds));
    if (nodeDetailPanelEnabled) tableStore.setExpandedCellViewOpen(true);
    setPendingFocus({ agentId, rowId: newRowIds[0] });
  };

  // Opened from the Design page's agent pills: select that agent, open its
  // card and bring it into view, once it's on the canvas.
  const pendingAgentReady = agents.some(
    (a) => a.id === tableStore.pendingCanvasAgentId
  );
  useEffect(() => {
    if (!pendingAgentReady) return;
    const agentId = tableStore.consumePendingCanvasAgent();
    if (!agentId) return;
    // A specific node in it ("Edit in …" from Agent settings): select it and
    // open its details too.
    const rowId = tableStore.consumePendingCanvasNode() ?? '';
    setAgentsVisible([agentId], true);
    setAgentsOpen([agentId], true);
    setSelectedAgentId(agentId);
    setPendingFocus({ agentId, rowId });
    if (rowId) onMemberClick(rowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAgentReady]);

  // ---- Canvas editing ----
  const editingEnabled =
    resolveBobcatAccess('canvasEditing', tableStore.featureFlags) ===
      'allowed' && !tableStore.isViewer;
  // Which toolbar form is open: a new agent (a group), or a new node that
  // goes into an agent or stays ungrouped.
  const [adding, setAdding] = useState<'agent' | 'node' | null>(null);
  const [newAgentName, setNewAgentName] = useState('');
  const [newNodeName, setNewNodeName] = useState('');
  const [newNodeTarget, setNewNodeTarget] = useState<string>('');

  // A new node or agent opens where you can fill it in: its card expanded,
  // the view on it, and (with the panel on) its details.
  const openNewRow = (agentId: string, rowId: string) => {
    landOn(agentId, [rowId]);
    onMemberClick(rowId);
  };

  const editing: CanvasEditing | undefined = editingEnabled
    ? {
        onAddNode: (agentId) => {
          const rowId = tableStore.addRowToGroup(agentId);
          if (rowId) openNewRow(agentId, rowId);
        },
        onDeleteAgent: (agentId) => {
          const title = agents.find((a) => a.id === agentId)?.title;
          tableStore.deleteRowGroup(agentId);
          if (selectedAgentId === agentId) setSelectedAgentId(null);
          sendToast({
            type: 'success',
            text: `Deleted ${title || 'the agent'}.`,
            autoClose: 3000,
          });
        },
        onDeleteNode: (rowId) => {
          if (tableStore.selectedNodeRowId === rowId) {
            tableStore.setExpandedCellViewOpen(false);
          }
          tableStore.deleteRows([rowId]);
        },
      }
    : undefined;

  const addAgent = () => {
    const created = tableStore.createCanvasAgent(newAgentName);
    setAdding(null);
    setNewAgentName('');
    if (created) openNewRow(created.groupId, created.rowId);
  };

  /**
   * A new Decision node, into the chosen agent (`addRowToGroup`, the same
   * call as Table View's group "add node" and the card's +) or, with no
   * agent chosen, ungrouped at the end of the table — where it shows in the
   * sidebar's Ungrouped list until someone groups it.
   */
  const addNode = () => {
    const nodeName = newNodeName.trim();
    const config = nodeName ? { nodeName } : undefined;
    setAdding(null);
    setNewNodeName('');
    if (newNodeTarget) {
      const rowId = tableStore.addRowToGroup(newNodeTarget, config);
      if (rowId) openNewRow(newNodeTarget, rowId);
      return;
    }
    const last = tableStore.rows[tableStore.rows.length - 1];
    if (!last) return;
    const rowId = tableStore.insertRowBelow(last.id, config);
    if (rowId) onMemberClick(rowId);
  };

  /**
   * Drawing Supervisor -> agent. There is no stored route to write: the
   * Supervisor reaches a use-case agent by matching what a customer says
   * against the example phrases on that agent's Decision nodes
   * (`addPhraseMatchedEdges`). So connecting opens the agent's first Decision
   * node to take those phrases — the edge appears once it has one.
   */
  const onConnect = ({
    source,
    target,
  }: {
    source: string | null;
    target: string | null;
  }) => {
    const from = agents.find((a) => a.id === source);
    const to = agents.find((a) => a.id === target);
    if (!from || !to || !isSupervisorCard(from) || to.category === 'core') {
      return;
    }
    const entry = to.members.find(
      (m) => String(m.nodeType ?? '').toLowerCase() === 'd'
    );
    if (!entry) {
      sendToast({
        type: 'warning',
        text: `${to.title} needs a Decision node for the Supervisor to route to. Add one first.`,
        autoClose: 4000,
      });
      return;
    }
    openNewRow(to.id, entry.id);
    sendToast({
      type: 'info',
      text: `Add example phrases to ${entry.label || 'this node'}: the Supervisor routes customers to ${to.title} when what they say matches.`,
      autoClose: 6000,
    });
  };

  const cancelAdd = () => setAdding(null);
  const addAgentControl = editingEnabled && (
    <div className="c-canvas-add-agent" data-testid="bobcat-canvas-add-agent">
      {adding === 'agent' && (
        <form
          className="c-canvas-add-agent__form"
          onSubmit={(e) => {
            e.preventDefault();
            addAgent();
          }}
        >
          <input
            autoFocus
            className="c-canvas-add-agent__input"
            data-testid="bobcat-canvas-add-agent-name"
            aria-label="New agent name"
            placeholder="Agent name, e.g. Order Status"
            value={newAgentName}
            onChange={(e) => setNewAgentName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancelAdd();
            }}
          />
          <Button size="small" type="submit">
            Add agent
          </Button>
          <Button size="small" variant="ghost" onClick={cancelAdd}>
            Cancel
          </Button>
        </form>
      )}
      {adding === 'node' && (
        <form
          className="c-canvas-add-agent__form"
          onSubmit={(e) => {
            e.preventDefault();
            addNode();
          }}
        >
          <input
            autoFocus
            className="c-canvas-add-agent__input"
            data-testid="bobcat-canvas-add-node-name"
            aria-label="New node name"
            placeholder="Node name (optional)"
            value={newNodeName}
            onChange={(e) => setNewNodeName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancelAdd();
            }}
          />
          <select
            className="c-canvas-add-agent__select"
            data-testid="bobcat-canvas-add-node-target"
            aria-label="Add the node to"
            value={newNodeTarget}
            onChange={(e) => setNewNodeTarget(e.currentTarget.value)}
          >
            <option value="">Ungrouped</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.title || 'Untitled agent'}
              </option>
            ))}
          </select>
          <Button size="small" type="submit">
            Add node
          </Button>
          <Button size="small" variant="ghost" onClick={cancelAdd}>
            Cancel
          </Button>
        </form>
      )}
      {adding === null && (
        <div className="c-canvas-add-agent__buttons">
          <Button
            size="small"
            variant="secondary"
            data-testid="bobcat-canvas-add-agent-open"
            onClick={() => setAdding('agent')}
          >
            <Icon name="add" slot="prefix" />
            Add agent
          </Button>
          <Button
            size="small"
            variant="secondary"
            data-testid="bobcat-canvas-add-node-open"
            onClick={() => {
              // Default to the agent in focus, when there is one.
              setNewNodeTarget(selectedAgentId ?? '');
              setAdding('node');
            }}
          >
            <Icon name="add" slot="prefix" />
            Add node
          </Button>
        </div>
      )}
    </div>
  );

  const onResourceDrop = (e: DragEvent) => {
    const drag = tableStore.resourceLibDragData;
    if (!isDraggingResource || !drag) return;
    const droppedOn = agentIdFromEvent(e);
    setDropOverAgentId(null);
    if (!droppedOn && !editingEnabled) return;
    e.preventDefault();
    tableStore.setResourceLibDragData(null);

    void (async () => {
      try {
        await uploadResourceLibScripts(
          scriptNamesFor(drag.data),
          handleUploadRef.current
        );
        // Dropped on empty canvas: the item starts a new agent, named for
        // it, whose starting Decision node is what the Supervisor will route
        // to (see `onConnect`).
        const agentId =
          droppedOn ??
          tableStore.createCanvasAgent(
            String(drag.data[0]?.nodeName ?? '').trim() || 'New agent'
          )?.groupId;
        if (!agentId) return;
        const newRowIds = tableStore.addResourceLibRowsToGroup(
          drag.data,
          agentId
        );
        if (newRowIds.length) landOn(agentId, newRowIds);
      } catch (err) {
        console.error(err);
        sendToast({
          type: 'error',
          text: 'Failed to add Resource Library',
          autoClose: 3000,
        });
      }
    })();
  };

  // Right-side panels close when you move to another screen; the canvas
  // leaving (a stage or Build view change) is that move.
  useEffect(() => () => tableStore.setResourceLibPanelOpen(false), []);

  // The ring fades once you've had a moment to find the new nodes.
  useEffect(() => {
    if (!freshRowIds.size) return undefined;
    const timer = window.setTimeout(() => setFreshRowIds(new Set()), 2400);
    return () => window.clearTimeout(timer);
  }, [freshRowIds]);

  // Drag end outside a card never fires `drop` here, so clear the hover too.
  useEffect(() => {
    if (!isDraggingResource) setDropOverAgentId(null);
  }, [isDraggingResource]);

  const showNodePanel =
    nodeDetailPanelEnabled &&
    tableStore.expandedCellViewOpen &&
    tableStore.selectedNodeRowId !== null;

  // Protolab's rendering rule (`cvEdges()`): intent edges always; error edges
  // behind a toggle (nearly every node has one); flow edges for the selected
  // agent. One exception to the last: a flow from one Use-Case Agent straight
  // into another (verification handing on to order status) is always drawn.
  // It is the only line that says one agent depends on another, and there are
  // few of them; the dense flows are the returns to the Supervisor and
  // Knowledge Agent, which stay selection-only.
  const coreIds = new Set(
    visibleAgents.filter((a) => a.category === 'core').map((a) => a.id)
  );
  const drawnWires = mergeWires(
    visibleEdges.filter((edge) => {
      if (edge.kind === 'error') return showErrorEdges;
      if (edge.kind === 'flow') {
        return (
          edge.source === selectedAgentId ||
          (!coreIds.has(edge.source) && !coreIds.has(edge.target))
        );
      }
      return true;
    }),
    edgeHops
  );
  // A Use-Case Agent nothing reaches — no intent, no flow, stored or
  // phrase-matched. Its card says so (dashed), so the missing line reads as
  // a gap in the solution rather than a gap in the drawing.
  const reachedIds = new Set(
    visibleEdges
      .filter((edge) => edge.kind !== 'error' && edge.source !== edge.target)
      .map((edge) => edge.target)
  );
  const unroutedAgentIds = new Set(
    visibleAgents
      .filter((a) => a.category !== 'core' && !reachedIds.has(a.id))
      .map((a) => a.id)
  );
  const tracingWire = drawnWires.find((w) => w.id === tracingWireId);
  const traceEnds = tracingWire
    ? new Set([tracingWire.source, tracingWire.target])
    : undefined;

  const { nodes, cards } = layoutAgentNodes({
    agents: visibleAgents,
    layoutEdges: visibleEdges,
    selectedAgentId,
    showMembers: nodeDetailPanelEnabled,
    onMemberClick,
    collapsedAgentIds: sidebarEnabled ? collapsedAgentIds : undefined,
    onToggleCollapse: sidebarEnabled ? toggleAgentOpen : undefined,
    showNodeTypeDots: sidebarEnabled,
    dimmedAgentIds,
    routingDimmedAgentIds,
    traceEnds,
    unroutedAgentIds,
    isDraggingResource,
    dropOverAgentId,
    freshRowIds,
    editing,
    onExplain: (agentId) => tableStore.setExplainAgentId(agentId),
  });

  // Glide to the first dropped node once the layout has placed it.
  useEffect(() => {
    if (!pendingFocus) return;
    const card = cards.find((c) => c.id === pendingFocus.agentId);
    if (!card) return;
    const y = card.rowY?.get(pendingFocus.rowId) ?? card.y + card.h / 2;
    setCenter(card.x + card.w / 2, y, {
      zoom: Math.max(getZoom(), 0.9),
      duration: 300,
    });
    setPendingFocus(null);
  }, [pendingFocus, cards, setCenter, getZoom]);

  const routed = new Map(routeWires(cards, drawnWires).map((w) => [w.id, w]));
  const titleOf = new Map(visibleAgents.map((a) => [a.id, a.title]));
  const showLabels =
    selectedAgentId !== null || drawnWires.length <= MAX_LABELLED_WIRES;

  const edges: Edge[] = drawnWires.flatMap((wire) => {
    const route = routed.get(wire.id);
    if (!route) return [];
    const hot = wire.id === tracingWireId;
    const selectDim =
      lit !== null && !lit.has(wire.source) && !lit.has(wire.target);
    const dim: WireDim = tracingWire
      ? hot
        ? 'none'
        : 'trace'
      : selectDim
        ? 'select'
        : 'none';
    const data: RoutedEdgeData = {
      kind: wire.kind,
      pts: route.pts,
      end: route.end,
      mid: route.mid,
      labelAt: route.label,
      labelAlign: route.labelAlign,
      label: wire.label,
      title:
        `${titleOf.get(wire.source) ?? ''} → ${titleOf.get(wire.target) ?? ''}` +
        (wire.labels.length > 1 ? `: ${wire.labels.join(', ')}` : '') +
        (wire.inferred ? ' (matched by example phrases)' : ''),
      showLabel: showLabels,
      dim,
      hot,
      onTrace: setTracingWireId,
    };
    return [
      {
        id: wire.id,
        source: wire.source,
        target: wire.target,
        type: 'routed',
        data,
      },
    ];
  });

  // Selecting a Use-Case Agent opens it and closes the other Use-Case
  // Agents, so a pick is a whole answer rather than one more open card;
  // selecting it again closes it. The Supervisor and Knowledge Agent are the
  // frame the rest is read against, so selection never closes them (the
  // prototype's `cvSelect`).
  const onNodeClick: NodeMouseHandler = (_, node) => {
    const agent = agents.find((a) => a.id === node.id);
    const deselecting = selectedAgentId === node.id;
    setSelectedAgentId(deselecting ? null : node.id);
    if (!agent || agent.category === 'core') return;
    setOpenOverrides((current) => {
      const next = new Map(current);
      if (deselecting) {
        next.set(agent.id, false);
      } else {
        agents
          .filter((a) => a.category !== 'core')
          .forEach((a) => next.set(a.id, a.id === agent.id));
      }
      return next;
    });
  };

  const ungroupedNote = ungroupedSummary
    ? `${ungroupedSummary.count} ungrouped ${
        ungroupedSummary.count === 1 ? 'node' : 'nodes'
      }`
    : null;

  // With the agent sidebar on, ungrouped nodes are its last section. Without
  // it they keep their old place: the `<ReactFlow>` `Panel` (a solution with
  // real groups) and the empty-state message (none).
  const ungroupedToggle = !sidebarEnabled && ungroupedNote && (
    <>
      <button
        type="button"
        className="c-canvas-ungrouped__toggle"
        data-testid="bobcat-canvas-ungrouped-toggle"
        onClick={() => tableStore.toggleShowUngroupedNodes()}
      >
        <TextBody size="small" variant="tertiary">
          <Icon
            name={showUngrouped ? 'chevron-down' : 'chevron-right'}
            size="xsmall"
          />{' '}
          {ungroupedNote}
        </TextBody>
      </button>
      {showUngrouped && (
        <div
          className="c-canvas-ungrouped__list"
          data-testid="bobcat-canvas-ungrouped-list"
        >
          {/* The first rows only: this is the no-sidebar fallback, and a
              solution with thousands of ungrouped nodes would stall it. */}
          {ungroupedNodes.slice(0, 200).map((node) => (
            <TextBody key={node.id} size="small">
              {node.nodeNumber ?? '—'} {node.label}
            </TextBody>
          ))}
        </div>
      )}
    </>
  );

  const hasFlow = visibleAgents.length > 0;

  const legend = sidebarEnabled && (
    <div className="c-canvas-legend" data-testid="bobcat-canvas-legend">
      <span className="c-canvas-legend__group">
        <b>Nodes</b>
        <span>
          <i className="c-canvas-legend__sw c-canvas-legend__sw--decision" />
          Decision
        </span>
        <span>
          <i className="c-canvas-legend__sw c-canvas-legend__sw--action" />
          Action
        </span>
      </span>
      <span className="c-canvas-legend__group">
        <b>Routing</b>
        <span>
          <i className="c-canvas-legend__ln c-canvas-legend__ln--intent" />
          Intent
        </span>
        <span>
          <i className="c-canvas-legend__ln c-canvas-legend__ln--flow" />
          Flow
        </span>
        <span>
          <i className="c-canvas-legend__ln c-canvas-legend__ln--error" />
          Error
        </span>
      </span>
    </div>
  );

  const libraryPanelOpen = inStage && tableStore.resourceLibPanelOpen;
  const flow = (
    <div
      className="c-canvas-view__flow"
      onDragOver={onResourceDragOver}
      onDragLeave={onResourceDragLeave}
      onDrop={onResourceDrop}
    >
      {agents.length === 0 ? (
        <div
          className="c-canvas-empty-state"
          data-testid="bobcat-canvas-empty-state"
        >
          <TextBody size="small" variant="tertiary">
            {ungroupedToggle
              ? 'No agent groups yet.'
              : 'No agent groups yet — group nodes in Table View to see them here.'}
          </TextBody>
          {/* The same one-click grouping Table View's controls bar offers
            (`autoAgentGroupingAvailable` is behind bobcat-auto-agent-grouping),
            offered where the empty canvas makes it obviously needed. A
            solution created before the wizard marked grouping on create lands
            here otherwise, with the fix a stage away. */}
          {!tableStore.isViewer && tableStore.autoAgentGroupingAvailable && (
            <Button
              size="medium"
              variant="secondary"
              data-testid="bobcat-canvas-apply-agent-grouping"
              onClick={() => tableStore.applyAutoAgentGrouping()}
            >
              <Icon name="microagent" slot="prefix" />
              Group Supervisor and Knowledge Agent
            </Button>
          )}
          {suggestAgents && ungroupedNodes.length >= SUGGEST_AGENTS_FROM && (
            <Button
              size="medium"
              variant="secondary"
              data-testid="bobcat-canvas-empty-suggest-agents"
              onClick={suggestAgents}
            >
              <Icon name="stars" slot="prefix" />
              Suggest agents from its {ungroupedNodes.length} nodes
            </Button>
          )}
          {addAgentControl}
          {ungroupedToggle}
        </div>
      ) : visibleAgents.length === 0 ? (
        <div
          className="c-canvas-empty-state"
          data-testid="bobcat-canvas-all-hidden"
        >
          <TextBody size="small" variant="tertiary">
            Every agent is hidden — check one in the sidebar to see it here.
          </TextBody>
        </div>
      ) : (
        <ReactFlow
          // No "React Flow" credit badge in the corner (MIT-licensed; the
          // library credits itself by default).
          proOptions={{ hideAttribution: true }}
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          onNodeClick={onNodeClick}
          // Clicking bare canvas clears the selection. A pan is a drag, not a
          // click, so React Flow never fires this for one.
          onPaneClick={() => setSelectedAgentId(null)}
          fitView
          fitViewOptions={FIT_VIEW_OPTIONS}
          minZoom={MIN_ZOOM}
          nodesDraggable={false}
          nodesConnectable={editingEnabled}
          onConnect={onConnect}
          // A user gesture carries an event; the fit and the drop's pan-to
          // (both programmatic) do not.
          onMoveStart={(event) => {
            if (event) userMovedView.current = true;
          }}
          elementsSelectable={false}
        >
          {addAgentControl && (
            <Panel position="top-left">{addAgentControl}</Panel>
          )}
          {/* Without the Build topbar (5-stage nav off) the error toggle
              keeps its old home. `.c-canvas-view` is then `position: fixed;
              inset: 0`, under the app header and the floating view-mode
              toggle — the offset clears both. */}
          {!inStage && (
            <Panel position="top-right" style={{ marginTop: 96 }}>
              <Checkbox
                checked={showErrorEdges}
                onChange={(e) =>
                  setShowErrorEdges(
                    (e.target as unknown as { checked: boolean }).checked
                  )
                }
              >
                Show error routing
              </Checkbox>
            </Panel>
          )}
          {ungroupedToggle && (
            <Panel position="bottom-left" data-testid="bobcat-canvas-ungrouped">
              {ungroupedToggle}
            </Panel>
          )}
          <Background gap={22} size={1} color="#dfe1e5" />
        </ReactFlow>
      )}
      {/* Browse the Library from the canvas too (the prototype's
          `.fab-library`). Inside the flow area, so the docked panels beside it
          push it aside rather than sit on it. */}
      {inStage && !!resourceLibraryData?.length && (
        <ResourceLibraryFab
          active={libraryPanelOpen}
          onClick={() =>
            tableStore.setResourceLibPanelOpen(!tableStore.resourceLibPanelOpen)
          }
        />
      )}
    </div>
  );

  const body = (
    <>
      {sidebarEnabled && (
        <AgentSidebar
          agents={agents}
          filterText={filterText}
          onFilterChange={setFilterText}
          hiddenAgentIds={hiddenAgentIds}
          onToggleAgent={toggleAgentVisible}
          onSetVisibility={setAgentsVisible}
          routedAgentIds={routedAgentIds}
          collapsedAgentIds={collapsedAgentIds}
          onToggleCollapse={toggleAgentOpen}
          onCollapseAll={() =>
            setAgentsOpen(
              agents.map((agent) => agent.id),
              false
            )
          }
          onExpandAll={() =>
            setAgentsOpen(
              agents.map((agent) => agent.id),
              true
            )
          }
          ungroupedNodes={ungroupedNodes}
          ungroupedOpen={showUngrouped}
          onToggleUngrouped={() => tableStore.toggleShowUngroupedNodes()}
          onUngroupedClick={nodeDetailPanelEnabled ? onMemberClick : undefined}
          onSuggestAgents={suggestAgents}
        />
      )}
      <div className="c-canvas-view__main">
        {flow}
        {legend}
      </div>
      {/* Build A: the same panel Table View docks, same viewer/editor gating,
          same close behaviour. */}
      {showNodePanel && (
        <div
          className="c-canvas-view__node-panel"
          data-testid="bobcat-canvas-node-panel"
        >
          <NodePropertyPanel
            viewOnly={tableStore.isViewer}
            onClose={() => tableStore.setExpandedCellViewOpen(false)}
          />
        </div>
      )}
      {/* The same panel Table View docks, opened by the same store state. */}
      {libraryPanelOpen && (
        <div
          className="c-canvas-view__library-panel"
          data-testid="bobcat-canvas-library-panel"
        >
          <ResourceLibPanel
            viewOnly={tableStore.isViewer}
            resourceLibData={resourceLibraryData}
            onClose={() => tableStore.setResourceLibPanelOpen(false)}
          />
        </div>
      )}
    </>
  );

  if (!renderTopbar) {
    return (
      <div className="c-canvas-view" data-testid="bobcat-canvas-view">
        {body}
      </div>
    );
  }

  return (
    <div
      className="c-canvas-view c-canvas-view--stage"
      data-testid="bobcat-canvas-view"
    >
      {renderTopbar(
        <CanvasControls
          showFind={sidebarEnabled}
          filterText={filterText}
          onFilterChange={setFilterText}
          showErrorEdges={showErrorEdges}
          onToggleErrorEdges={() => setShowErrorEdges((on) => !on)}
          showZoom={hasFlow}
        />
      )}
      <div className="c-canvas-view__row">{body}</div>
    </div>
  );
});
